(function () {
  const SELECTOR_SECTION = "[data-interactive-collections]";
  const SELECTOR_BLOCK = "[data-interactive-collection-block]";
  const SELECTOR_CURSOR = "[data-interactive-cursor-card]";
  /** Follow-cursor preview: desktop ($md+) with mouse / fine pointer only */
  const MQ_CURSOR = "(min-width: 750px) and (hover: hover) and (pointer: fine)";
  /** Smooth follow (reference: card eases toward cursor) */
  const LERP = 0.14;
  const LERP_EPS = 0.35;

  function initBlock(block) {
    const cursorCard = block.querySelector(SELECTOR_CURSOR);
    if (!cursorCard || block.dataset.interactiveCursorBound === "1") return;
    if (!window.matchMedia(MQ_CURSOR).matches) return;
    block.dataset.interactiveCursorBound = "1";

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const infoEl = block.querySelector(".collection-card__information");

    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;
    let rafId = null;
    /** Cached for applyPositionFromLocal — avoids getBoundingClientRect(cursor) each RAF (Safari jitter) */
    let cachedBlockHeight = 0;
    let cachedInfoTop = 0;
    let cachedCursorHalfH = 96;

    const syncLayoutMetrics = () => {
      const br = block.getBoundingClientRect();
      cachedBlockHeight = br.height;
      if (infoEl) {
        const ir = infoEl.getBoundingClientRect();
        cachedInfoTop = ir.top - br.top;
      }
      const ch = cursorCard.offsetHeight;
      if (ch > 2) {
        cachedCursorHalfH = ch / 2;
      }
    };

    const isPointerOverInformation = (event) => {
      if (!infoEl) return false;
      const r = infoEl.getBoundingClientRect();
      return (
        event.clientX >= r.left &&
        event.clientX <= r.right &&
        event.clientY >= r.top &&
        event.clientY <= r.bottom
      );
    };

    const syncCursorOverInformationClass = (event) => {
      block.classList.toggle("is-cursor-over-information", isPointerOverInformation(event));
    };

    const applyPositionFromLocal = (x, y) => {
      const cx = x;
      let maxCy = cachedBlockHeight;
      if (infoEl) {
        const maxAboveInfo = cachedInfoTop - cachedCursorHalfH;
        if (Number.isFinite(maxAboveInfo)) {
          maxCy = Math.min(maxCy, maxAboveInfo);
        }
      }
      const cy = Math.min(y, maxCy);
      cursorCard.style.setProperty("--ic-cursor-x", `${cx}px`);
      cursorCard.style.setProperty("--ic-cursor-y", `${cy}px`);
    };

    const updateTargetFromEvent = (event) => {
      const rect = block.getBoundingClientRect();
      targetX = event.clientX - rect.left;
      targetY = event.clientY - rect.top;
    };

    function tick() {
      rafId = null;
      const dx = targetX - currentX;
      const dy = targetY - currentY;
      if (dx * dx + dy * dy < LERP_EPS * LERP_EPS) {
        currentX = targetX;
        currentY = targetY;
        applyPositionFromLocal(currentX, currentY);
        return;
      }
      currentX += dx * LERP;
      currentY += dy * LERP;
      applyPositionFromLocal(currentX, currentY);
      rafId = window.requestAnimationFrame(tick);
    }

    function scheduleFollow() {
      if (reduceMotion) {
        currentX = targetX;
        currentY = targetY;
        syncLayoutMetrics();
        applyPositionFromLocal(currentX, currentY);
        return;
      }
      if (rafId == null) {
        rafId = window.requestAnimationFrame(tick);
      }
    }

    const positionAtPointer = (event) => {
      syncLayoutMetrics();
      updateTargetFromEvent(event);
      scheduleFollow();
    };

    const onDocPointerMove = (event) => {
      if (reduceMotion) return;
      syncCursorOverInformationClass(event);
      /* While mouse/touch buttons are pressed (drag, scrollbar, text selection), skip follow — avoids Safari jitter */
      if (event.buttons !== 0) return;
      if (isPointerOverInformation(event)) return;
      positionAtPointer(event);
    };

    const onBlockPointerMoveReduced = (event) => {
      syncCursorOverInformationClass(event);
    };

    const onLayoutRefresh = () => {
      syncLayoutMetrics();
      applyPositionFromLocal(currentX, currentY);
    };

    const onEnter = (event) => {
      block.classList.add("is-hovering");
      syncCursorOverInformationClass(event);
      if (reduceMotion) return;
      document.addEventListener("pointermove", onDocPointerMove, { passive: true });
      document.addEventListener("mousemove", onDocPointerMove, { passive: true });
      window.addEventListener("scroll", onLayoutRefresh, { passive: true, capture: true });
      window.addEventListener("resize", onLayoutRefresh, { passive: true });
      window.requestAnimationFrame(() => {
        syncLayoutMetrics();
        syncCursorOverInformationClass(event);
        if (!isPointerOverInformation(event)) {
          updateTargetFromEvent(event);
          currentX = targetX;
          currentY = targetY;
          applyPositionFromLocal(currentX, currentY);
        } else {
          updateTargetFromEvent(event);
          currentX = targetX;
          currentY = targetY;
        }
        window.requestAnimationFrame(() => {
          syncLayoutMetrics();
          applyPositionFromLocal(currentX, currentY);
        });
      });
    };

    const onLeave = () => {
      if (rafId != null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
      block.classList.remove("is-hovering", "is-cursor-over-information");
      document.removeEventListener("pointermove", onDocPointerMove);
      document.removeEventListener("mousemove", onDocPointerMove);
      block.removeEventListener("pointermove", onBlockPointerMoveReduced);
      window.removeEventListener("scroll", onLayoutRefresh, true);
      window.removeEventListener("resize", onLayoutRefresh);
    };

    const onEnterReduced = (event) => {
      block.classList.add("is-hovering");
      syncCursorOverInformationClass(event);
      const rect = block.getBoundingClientRect();
      window.requestAnimationFrame(() => {
        syncLayoutMetrics();
        targetX = currentX = rect.width / 2;
        targetY = currentY = rect.height / 2;
        applyPositionFromLocal(currentX, currentY);
      });
      block.addEventListener("pointermove", onBlockPointerMoveReduced, { passive: true });
    };

    if (reduceMotion) {
      block.addEventListener("pointerenter", onEnterReduced);
      block.addEventListener("pointerleave", onLeave);
    } else {
      block.addEventListener("pointerenter", onEnter);
      block.addEventListener("pointerleave", onLeave);
    }
  }

  function initSection(section) {
    if (section.dataset.cursorEnabled !== "true") return;
    section.querySelectorAll(SELECTOR_BLOCK).forEach(initBlock);
  }

  function initAll() {
    document.querySelectorAll(SELECTOR_SECTION).forEach(initSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    const section =
      event.target.querySelector?.(SELECTOR_SECTION) ||
      event.target.closest?.("section")?.querySelector(SELECTOR_SECTION);
    if (section) {
      initSection(section);
      if (
        typeof window.initArrowMoveOnHover === "function" &&
        section.querySelector(".interactive-collections__block")
      ) {
        window.initArrowMoveOnHover(section, ".interactive-collections__block");
      }
    }
  });
})();
